import { TiptapContent, TiptapNodeType } from '../../tiptapTypes';
import { ImMap } from '../../../utils/immutableHelper';
import { RawDraftContentState } from 'draft-js';
import { DocumentType } from './elementConversion';
import { useMemo } from 'react';
import { useElementTiptapContent } from './useElementTiptapContent';

// NOTE - It's 60, however a wrapped line is 1.5x the height of a single line,
//  so we assume 2 wrapped lines equates to another paragraph height-wise.
const DEFAULT_CHARS_PER_LINE = 120;

/**
 * If a paragraph has a lot of text in it, it can take up multiple lines, so we should adjust the
 * paragraph limit accordingly. This function checks the first few paragraphs and adjusts the limit
 * downwards if it finds any that are long.
 */
const getAdjustedLimit = (paragraphLimit: number, charsPerLine: number, fullContent: TiptapContent) => {
    let remaining = paragraphLimit;

    // Only check up to the paragraph limit, as anything beyond that won't be rendered anyway
    const nodesToCheck = fullContent.content.slice(0, paragraphLimit);

    for (let i = 0; i < nodesToCheck.length; i++) {
        const node = nodesToCheck[i];

        // Only count text in paragraphs
        if (node.type !== TiptapNodeType.paragraph || !node.content) continue;

        let textLen = 0;

        for (const child of node.content) {
            if (!child.text) continue;
            textLen += child.text.length;
        }

        const decrement = Math.floor(textLen / charsPerLine);

        // 0 indexed, so need to add 1
        const currentNodeCount = i + 1;
        remaining = Math.max(currentNodeCount, remaining - decrement);

        if (remaining === currentNodeCount) break;
    }

    return remaining;
};

/**
 * The same as useElementTiptapContent, but limits the content to a certain number of characters.
 * This is useful for previews, where we don't want to render the entire content of a large document.
 */
export const useLimitedElementTiptapContent = (
    paragraphLimit: number,
    charsPerLine: number = DEFAULT_CHARS_PER_LINE,
) => {
    return (
        textContent: ImMap<TiptapContent> | ImMap<RawDraftContentState> | string | null,
        elementId?: string,
        documentType?: DocumentType,
    ) => {
        const fullContent = useElementTiptapContent(textContent, elementId, documentType);

        return useMemo(() => {
            if (!fullContent.content || fullContent.content.length <= paragraphLimit) return fullContent;

            const adjustedLimit = getAdjustedLimit(paragraphLimit, charsPerLine, fullContent);

            return {
                ...fullContent,
                content: fullContent.content.slice(0, adjustedLimit),
            };
        }, [fullContent, paragraphLimit]);
    };
};
